import {
  Box, Stack, useMediaQuery, Collapse, Tabs, Tab,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Languages, Calendar, 
  AlertTriangle
} from "lucide-react";
import { useEffect, useState } from "react";
import { IDataMonth, IMonthsData } from "./CalendarioObblighi.types";
import {
  activeMonthData,
  checkPvr, HEADERS_LABEL, languagesString, 
  sortByMonthOrder } from "./utils";
import { Spinner } from "../../components";
import { BadgeBox } from "../../components/Commons/BadgeBox";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { TopicsItem } from "../../components/Commons/TopicsItem";
import { VerticalDivider } from "../../components/Divider/VerticalDivider";
import { TypographyExploreCardText } from "../../styles/Typography/TypographyExploreCardText.styles";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";

export const RowList = () => {


  const [showTable, setShowTable] = useState(false);
  const [formatError, setFormatError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [monthsData, setMonthsData] = useState<IMonthsData[]>([]);
  const [activeMonth, setActiveMonth] = useState<string>("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const loadCsvData = async () => {
    try {
      setIsLoading(true);

      //   const response = await fetch(import.meta.env.VITE_CALENDARIO_OBBLIGHI_URL);
      const response = await fetch(import.meta.env.VITE_CALENDARIO_OBBLIGHI_URL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      // Leggi il file come testo per CSV
      const text = await response.text();
      const lines = text.split("\n").filter(line => line.trim() !== "");
      
      // Parser CSV semplice per formato senza virgolette con separatore punto e virgola (;)
      const parsedData = lines.map(line => {
        return line.split(";").map(cell => cell.trim());
      });
      if(parsedData[0].length < 9) {
        setFormatError(true);
        throw new Error("Il file CSV non è nel formato atteso");
      }

      const newOrder = sortByMonthOrder(parsedData);
      setMonthsData(newOrder);
  
      if (newOrder.length > 0) {
        setActiveMonth(newOrder[0].label);
        setShowTable(true);
      }
  
    } catch (error) {
      console.error("Errore nel leggere il file CSV:", error);
      setMonthsData([]);
    } finally{
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadCsvData();
  }, []);

  if(isLoading) return <Spinner />;

  if(monthsData.length === 0 && !formatError) return (
    <Stack  flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
      <AlertTriangle color={ theme.customColors.textWarning } />
      <Typography color={ theme.customColors.textWarning }>Nessun dato disponibile</Typography>
    </Stack>
  );

  if(formatError) return (
    <Stack  flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
      <AlertTriangle color={ theme.customColors.textWarning } />
      <Typography color={ theme.customColors.textWarning }>Errore nel formato del file CSV, controllare che tutte le colonne siano presenti nel file e in ordine atteso</Typography>
    </Stack>
  )
  ;

  return (
    <>
      { /* Tabelle con i dati */ }
      <Collapse in={ showTable } timeout="auto" unmountOnExit sx={ { width: "100%" } }>
        <Box sx={ { mt: 4 } }>
          { /* Tabs per i mesi */ }
          <Box sx={ { borderColor: "divider", mb: 3 } }>
            <Tabs
              value={ activeMonth }
              onChange={ (_, newValue) => setActiveMonth(newValue) }
              variant={ "scrollable" }
              scrollButtons={ "auto" }
              TabIndicatorProps={ { style: { background: theme.customColors.accentVariantA, borderRadius: "1.5rem" } } }
              allowScrollButtonsMobile={ isMobile }
              sx={ {
                ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
                ".Mui-selected": { color: theme.customColors.accentVariantA },
                ".Mui-disabled.MuiTabs-scrollButtonsHideMobile": { opacity: "0.4 !important" },
                fontFamily: "Inter"
              } }
            >
              { monthsData.map(({ label }) => (
                <Tab
                  key={ label + "-tab" }
                  label={ label }
                  value={ label }
                  sx={ {
                    textTransform: "none",
                    fontSize: "0.875rem",
                    letterSpacing: "0.0313em",
                    fontWeight: 600,
                    fontStyle: "semibold",
                    height: "1.5rem",
                    minWidth: "3rem",
                    marginX:"0.35rem",
                    lineHeight: "1.5rem",
                    borderBottom: `2px solid ${theme.customColors.border}`,
                    "&.Mui-focusVisible": {
                      border: `1px solid ${theme.customColors.accentVariantA}`
                    }
                  } }
                />
              )) }
            </Tabs>
          </Box>
          <Stack id="months-data-container" rowGap="0.5rem">
            { activeMonth && activeMonthData(monthsData, activeMonth)?.map((rowData: IDataMonth, index) => (
              <CommonWidgetContainer key={ index + activeMonth } hoverBorderAccent={ false } width="unset !important" maxWidth="100% !important" sx={ {
                gap:"0.75rem"
              } }
              >
                <Stack 
                  className="header-info" 
                  justifyContent={ "space-between" } 
                  flexDirection={ !isMobile ? "row" : "column-reverse" } 
                  gap="1rem"  
                >
                  { /* LEFT INFOs */ }
                  <Box display="flex" flexDirection="row" gap="0.25rem" alignItems="center" sx={ { opacity: 0.7 } }>
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.CODE_SEL] }
                    </TypographyExploreCardText>
                    <VerticalDivider />
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.AMBITO] }
                    </TypographyExploreCardText>
                    <VerticalDivider />
                    <Languages size={ "1rem" } style={ {
                      position: "relative",
                      top:"20%"
                    } } />
                    { " " }
                    <TypographyExploreCardText>
                      { languagesString(rowData[HEADERS_LABEL.ITALIAN], rowData[HEADERS_LABEL.ENGLISH]) }
                    </TypographyExploreCardText>
                  </Box>
                  { /* RIGHT INFOs */ }
                  <Box display="flex" flexDirection="row" gap="0.25rem" alignItems="center" sx={ {
                    color:theme.customColors.textMandatory,
                    fontSize: "0.75rem",
                    fontWeight: 700
                  } }> 
                    <Calendar size="0.85rem"/>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      { rowData[HEADERS_LABEL.MONTH] }
                    </TypographyTextTag>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      |
                    </TypographyTextTag>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      { "Decade"  + " " + rowData[HEADERS_LABEL.DECADE] }
                    </TypographyTextTag>
                  </Box>
                </Stack>
                <Stack className="body-info">
                  { /* TITLE */ }
                  <TypographyExploreCardText fontWeight={ 700 } fontSize={ "1rem" } fontStyle="bold">
                    { rowData[HEADERS_LABEL.TITLE_SELECTION] }
                  </TypographyExploreCardText>
                </Stack>
                { /* TAGS AND BADGES */ }
                <Stack className="footer-info" flexDirection="row" gap="0.85rem">
                  <TopicsItem i={ 0 }>
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.DESTINATARI] }
                    </TypographyExploreCardText>
                  </TopicsItem>
                  { checkPvr(rowData[HEADERS_LABEL.PVR]) && (
                    <BadgeBox 
                      background={ theme.customColors.backgroundSecondary }
                      textColor={ theme.customColors.textTag }
                      border={ `1px solid ${theme.customColors.textTag}` }> 
                      <TypographyTextTag>
                        { "PVR 2025" }
                      </TypographyTextTag>
                    </BadgeBox>
                  ) }
                </Stack>
              </CommonWidgetContainer>
            )) }
          </Stack>
        </Box>
      </Collapse>
    </>
  );
};
